import type { api } from "./api";
import { OCCUPANCY_COLORS, REGISTRATION_STATUS_COLORS } from "./color";

export type ColorMode = "occupancy" | "registrationStatus" | "region" | "bezirk";
type Seat = Awaited<ReturnType<typeof api.sessionSeating>>["seats"][number];

export interface ColorScales {
  /** Region name -> color. Used as the fill in both "region" and "bezirk" mode. */
  region: Map<string, string>;
  /** Bezirk name -> color, drawn from its own independent palette (NOT nested under the region hue) — used only as the seat's border in "bezirk" mode. */
  bezirkBorder: Map<string, string>;
}

const NO_DATA_FILL = "#d8dbe2";
// A seated occupant whose record is simply missing region/bezirk data (a
// data-quality gap in the participant source, not an unoccupied seat) needs
// a visibly different fill than NO_DATA_FILL — otherwise it's
// indistinguishable from a genuinely free seat. Deliberately a low-chroma
// slate rather than a hue from the region palette (which always uses c
// 38-74, see color.ts) so it can never coincide with a real region's color.
export const NO_REGION_OCCUPIED_FILL = "#5b6270";

export function seatFill(seat: Seat, mode: ColorMode, scales: ColorScales): string {
  if (mode === "occupancy") {
    if (seat.status !== "available" && seat.status !== "reserved") return OCCUPANCY_COLORS[seat.status] ?? OCCUPANCY_COLORS.blocked;
    return seat.occupant ? OCCUPANCY_COLORS.occupied : OCCUPANCY_COLORS.available;
  }
  if (mode === "registrationStatus") {
    if (!seat.occupant) return REGISTRATION_STATUS_COLORS.none;
    return REGISTRATION_STATUS_COLORS[seat.occupant.registrationStatus ?? "none"] ?? REGISTRATION_STATUS_COLORS.none;
  }
  // region AND bezirk both fill by region — bezirk adds its own border color on
  // top (see seatBorder below). Two bezirke of the same region would otherwise
  // need near-identical nested hues to stay "grouped", which is exactly what
  // made them hard to tell apart.
  if (!seat.occupant?.region) return seat.occupant ? NO_REGION_OCCUPIED_FILL : NO_DATA_FILL;
  return scales.region.get(seat.occupant.region) ?? NO_REGION_OCCUPIED_FILL;
}

/** Only meaningful in "bezirk" mode: undefined elsewhere so the caller falls back to the default neutral seat border. */
export function seatBorder(seat: Seat, mode: ColorMode, scales: ColorScales): string | undefined {
  if (mode !== "bezirk" || !seat.occupant?.bezirk) return undefined;
  return scales.bezirkBorder.get(seat.occupant.bezirk);
}

/**
 * A seat that can't be assigned to anyone (blocked/technical/emergency —
 * matches the "gesperrt" grouping seatService already uses for its
 * seat_blocked warning). Callers mark these with their own icon regardless
 * of colorMode, since in region/bezirk/registrationStatus mode an unoccupied
 * blocked seat would otherwise render identically to a merely-empty one.
 */
export function isNonSeatable(seat: Seat): boolean {
  return seat.status !== "available" && seat.status !== "reserved";
}
